import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useBlockPartner } from '../../../../hooks/partners/useBlockPartner'
import { useAcceptPartner } from '../../../../hooks/partners/useAcceptPartner'
import { useDeletePartner } from '../../../../hooks/partners/useDeletePartner'
import { useRejectPartner } from '../../../../hooks/partners/useRejectPartner'
import { useUpdatePartner } from '../../../../hooks/partners/useUpdatePartner'
import { useUnblockPartner } from '../../../../hooks/partners/useUnblockPartner'
import { useCancelPartnerInvitation } from '../../../../hooks/partners/useCancelPartnerInvitation'

import { Tabs } from '../../../tabs/Tabs'
import PartnerCard from './partnerCard/PartnerCard'
import { ListingDetails } from '../../../listing/ListingDetails'

import { useApp } from '../../../../contexts/AppContext'
import { useConfirm } from '../../../../contexts/ConfirmContext'
import {
  DisplayType,
  SortDirectionType,
  SortType,
} from '../../../../types/listing'
import { ConfirmLayout } from '../../../../types/confirm/confirm'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { PartnerModel, PartnerStatus } from '../../../../types/court/court'
import { ActionType } from '../../../../constants/actionTypes'
import { CONFIG } from '../../../../config'

export type PartnerFilter = 'ALL' | 'FAVORITES' | PartnerStatus

type Props = {
  refreshTrigger?: boolean
  statusFilter: PartnerFilter
  onStatusFilterChange: (filter: PartnerFilter) => void
  onPartnersChange?: () => void
}

const PartnerList = ({
  refreshTrigger = false,
  statusFilter,
  onStatusFilterChange,
  onPartnersChange,
}: Props) => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { confirm } = useConfirm()
  const { t } = useTranslation('partners')
  const [localRefresh, setLocalRefresh] = useState(false)
  const { deletePartner } = useDeletePartner()
  const { updatePartner, error: updateError } = useUpdatePartner()
  const { acceptPartner, error: acceptError } = useAcceptPartner()
  const { rejectPartner, error: rejectError } = useRejectPartner()
  const {
    cancelPartnerInvitation,
    error: cancelError,
  } = useCancelPartnerInvitation()
  const { blockPartner, error: blockError } = useBlockPartner()
  const { unblockPartner, error: unblockError } = useUnblockPartner()

  const filterOptions = useMemo(
    () => [
      { value: 'ALL', label: t('partners.filters.all') },
      { value: 'FAVORITES', label: t('partners.filters.favorites') },
      { value: PartnerStatus.LINKED, label: t('partners.card.linked') },
      {
        value: PartnerStatus.REQUEST_RECEIVED,
        label: t('partners.card.request-received'),
        mobileLabel: t('partners.filters.request-received-short'),
      },
      {
        value: PartnerStatus.REQUEST_SENT,
        label: t('partners.filters.request-sent'),
        mobileLabel: t('partners.filters.request-sent-short'),
      },
      {
        value: PartnerStatus.BLOCKED,
        label: t('partners.filters.blocked'),
      },
    ],
    [t]
  )

  useEffect(() => {
    const message =
      updateError ??
      acceptError ??
      rejectError ??
      cancelError ??
      blockError ??
      unblockError
    if (message) showBanner({ kind: BannerKind.ERR, msg: message })
  }, [
    updateError,
    acceptError,
    rejectError,
    blockError,
    unblockError,
    cancelError,
    showBanner,
  ])
  const handleRefresh = () => {
    setLocalRefresh(previous => !previous)
    onPartnersChange?.()
  }

  const handleDelete = (partner: PartnerModel) =>
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('partners.delete-confirmation.title'),
      subtitle: t('partners.delete-confirmation.subtitle'),
      confirmLabel: t('partners.delete-confirmation.confirm'),
      backLabel: t('partners.delete-confirmation.cancel'),
      returnTo: '/partners',
      itemCards: [
        {
          id: partner.id,
          title: `${partner.firstName} ${partner.lastName}`,
          details: [
            {
              label: t('partners.delete-confirmation.email-label'),
              value: partner.email,
            },
            {
              label: t('partners.delete-confirmation.status-label'),
              value:
                partner.status === PartnerStatus.LINKED
                  ? t('partners.card.linked')
                  : t('partners.card.invite-pending'),
            },
            {
              label: t('partners.card.games-played'),
              value: String(partner.gamesPlayed),
            },
          ],
        },
      ],
      onConfirm: async () => {
        await deletePartner(partner.id)
        showBanner({
          kind: BannerKind.OK,
          msg: t('partners.hook.successfully-deleted'),
        })
        handleRefresh()
      },
    })

  const handleBlock = (partner: PartnerModel) =>
    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('partners.block-confirmation.title'),
      subtitle: t('partners.block-confirmation.subtitle'),
      confirmLabel: t('partners.block-confirmation.confirm'),
      backLabel: t('partners.block-confirmation.cancel'),
      returnTo: '/partners',
      itemCards: [
        {
          id: partner.id,
          title: `${partner.firstName ?? ''} ${partner.lastName ?? ''}`.trim(),
          details: [
            {
              label: t('partners.delete-confirmation.email-label'),
              value: partner.email,
            },
          ],
        },
      ],
      onConfirm: async () => {
        if (await blockPartner(partner.id)) {
          showBanner({
            kind: BannerKind.OK,
            msg: t('partners.hook.successfully-blocked'),
          })
          handleRefresh()
        }
      },
    })

  const handleAction = async (partner: PartnerModel, action: ActionType) => {
    switch (action) {
      case ActionType.BOOK:
        navigate('/booking')
        return
      case ActionType.DELETE:
        handleDelete(partner)
        return
      case ActionType.FAVORITE: {
        const updatedPartner = await updatePartner(partner.id)
        if (updatedPartner) {
          showBanner({
            kind: BannerKind.OK,
            msg: t('partners.hook.successfully-updated'),
          })
          handleRefresh()
        }
        return
      }
      case ActionType.ACCEPT: {
        const acceptedPartner = await acceptPartner(partner.id)
        if (acceptedPartner) {
          showBanner({
            kind: BannerKind.OK,
            msg: t('partners.hook.successfully-accepted'),
          })
          handleRefresh()
        }
        return
      }
      case ActionType.REJECT: {
        if (await rejectPartner(partner.id)) {
          showBanner({
            kind: BannerKind.OK,
            msg: t('partners.hook.successfully-rejected'),
          })
          handleRefresh()
        }
        return
      }
      case ActionType.CANCEL_REQUEST: {
        if (await cancelPartnerInvitation(partner.id)) {
          showBanner({
            kind: BannerKind.OK,
            msg: t('partners.hook.successfully-canceled'),
          })
          handleRefresh()
        }
        return
      }
      case ActionType.BLOCK: {
        handleBlock(partner)
        return
      }
      case ActionType.UNBLOCK: {
        if (await unblockPartner(partner.id)) {
          showBanner({
            kind: BannerKind.OK,
            msg: t('partners.hook.successfully-unblocked'),
          })
          handleRefresh()
        }
        return
      }
      default:
        return
    }
  }

  const renderPartner = (partner: PartnerModel) => (
    <PartnerCard partner={partner} onAction={handleAction} />
  )

  const handleListError = (message: string) => {
    showBanner({
      kind: BannerKind.ERR,
      msg: message,
    })
  }

  return (
    <ListingDetails<PartnerModel>
      endpoint={`${CONFIG.BOUNCE_API}/api/v1/partners`}
      additionalParams={
        statusFilter === 'ALL'
          ? {}
          : statusFilter === 'FAVORITES'
            ? { favorite: 'true' }
            : { status: statusFilter }
      }
      refreshTrigger={refreshTrigger !== localRefresh}
      defaultSortField={SortType.NAME}
      defaultSortDirection={SortDirectionType.ASC}
      renderItem={renderPartner}
      onError={handleListError}
      filter={
        <Tabs
          tab={statusFilter}
          tabs={filterOptions}
          onChange={value => onStatusFilterChange(value as PartnerFilter)}
        />
      }
      initialViewMode={DisplayType.GRID}
      gridItemClassName="col-12 col-md-6 col-xxl-4"
    />
  )
}
export default PartnerList
